import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Chip,
  Button, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, IconButton, Tooltip,
  LinearProgress, Stack, Divider, TextField, MenuItem } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import TimelineIcon from '@mui/icons-material/Timeline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  createKnowledgeSpaceSource,
  getIndexDetail,
  listCollectionFiles,
  downloadCollectionFile,
  listIndexAssets,
  listIndexRuns,
  listKnowledgeSpaceSources,
  testIndexQuery,
} from "../api/agentApi";
import UpdateIndexButton from './DocumentIndexDetails/UpdateIndexButton';
import { v4 as uuidv4 } from 'uuid';
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";

const POLL_INTERVAL_BASE = 5000; // ms
const ACTIVE_INDEX_STATUSES = new Set(['pending', 'queued', 'running', 'processing']);

const colors = {
  ink: "#101828",
  muted: "#6F6A64",
  border: "#E7D8CA",
  line: "#EFE5DD",
  copper: BRAND_COLORS.primary.main,
  copperDark: BRAND_COLORS.primary.dark,
  copperSoft: BRAND_COLORS.primary.soft,
  panel: "#FFFDFB",
  green: "#4F7D4A",
  greenSoft: "#EEF7ED",
  blue: "#3D6E9F",
  blueSoft: "#EAF3FA",
  amber: "#A66A21",
  amberSoft: "#FFF6E5",
  red: "#B42318",
  redSoft: "#FDECEC",
};

const formControlSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
    bgcolor: "#fff",
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: BRAND_COLORS.primary.border },
    "&.Mui-focused fieldset": { borderColor: colors.copper },
  },
};

const statusTone = {
  completed: { label: "Ready", color: colors.green, bg: colors.greenSoft, icon: CheckCircleOutlineIcon },
  failed: { label: "Needs attention", color: colors.red, bg: colors.redSoft, icon: ErrorOutlineIcon },
  queued: { label: "Queued", color: colors.amber, bg: colors.amberSoft, icon: HourglassEmptyIcon },
  pending: { label: "Pending", color: colors.amber, bg: colors.amberSoft, icon: HourglassEmptyIcon },
  running: { label: "Processing", color: colors.blue, bg: colors.blueSoft, icon: RefreshIcon },
  processing: { label: "Processing", color: colors.blue, bg: colors.blueSoft, icon: RefreshIcon },
};

function normalizeStatus(status) {
  return String(status || "unknown").toLowerCase();
}

function getStatusTone(status) {
  return statusTone[normalizeStatus(status)] || {
    label: status || "Unknown",
    color: colors.muted,
    bg: "#F1EFED",
    icon: FolderOpenOutlinedIcon,
  };
}

function formatDate(value) {
  if (!value) return "--";
  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "--";
  }
}

function clampPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function getProgressSummary(index) {
  const progress = index?.progress || {};
  const total = Number(progress.total || 0);
  return {
    total,
    percent: clampPercent(progress.percent),
    label: progress.label || (total > 0 ? `${clampPercent(progress.percent)}% complete` : "Preparing files..."),
    determinate: total > 0,
  };
}

function formatCount(value) {
  const n = Number(value || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function formatBytes(value) {
  const n = Number(value || 0);
  if (!n) return "--";
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function sourceLabel(source) {
  const path = typeof source === "object" ? source.path : source;
  if (!path) return "Source";
  const clean = String(path).split("?")[0];
  return clean.split("/").filter(Boolean).slice(-2).join("/") || clean;
}

function readableSourceType(value) {
  return String(value || "source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function knowledgeSourceLabel(source) {
  return source?.display_name || source?.backing_ref?.tool_name || readableSourceType(source?.source_type);
}

function ModalityIcon({ modality }) {
  const normalized = String(modality || "text").toLowerCase();
  if (normalized === "image") return <ImageOutlinedIcon fontSize="small" />;
  if (normalized === "video") return <MovieOutlinedIcon fontSize="small" />;
  return <InsertDriveFile fontSize="small" />;
}

function StatusChip({ status }) {
  const tone = getStatusTone(status);
  const StatusIcon = tone.icon;
  return (
    <Chip
      icon={<StatusIcon />}
      label={tone.label}
      sx={{
        bgcolor: tone.bg,
        color: tone.color,
        border: `1px solid ${tone.color}33`,
        borderRadius: UI_RADIUS.chip,
        fontWeight: 900,
        "& .MuiChip-icon": { color: tone.color },
      }}
    />
  );
}

function MetricTile({ label, value, subtext, icon: Icon, tone = colors.copper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${colors.border}`,
        bgcolor: "#fff",
        transition: "all 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 14px 28px rgba(40, 28, 18, 0.08)",
          borderColor: "rgba(184, 115, 51, 0.42)",
        },
      }}
    >
      <Stack spacing={1}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: UI_RADIUS.panel,
            bgcolor: `${tone}16`,
            color: tone,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 800 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: colors.ink, fontWeight: 900 }}>{value}</Typography>
        {subtext && <Typography variant="body2" sx={{ color: colors.muted }}>{subtext}</Typography>}
      </Stack>
    </Paper>
  );
}

function CompactMetric({ label, value, subtext, icon: Icon, tone = colors.copper }) {
  return (
    <Box
      sx={{
        p: 1.4,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${colors.line}`,
        bgcolor: colors.panel,
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: UI_RADIUS.panel,
            bgcolor: `${tone}14`,
            color: tone,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "0 0 auto",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 900, textTransform: "uppercase" }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ color: colors.ink, fontWeight: 900, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {subtext && <Typography variant="caption" sx={{ color: colors.muted }}>{subtext}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
}

function getIndexProgressSignature(index) {
  if (!index) return "";
  const sourcePath = Array.isArray(index.source_path) ? index.source_path : [];
  const progress = index.progress || {};
  return JSON.stringify({
    status: index.status || "",
    message: index.message || "",
    progress: {
      percent: progress.percent || 0,
      total: progress.total || 0,
      completed: progress.completed || 0,
      failed: progress.failed || 0,
      running: progress.running || 0,
      pending: progress.pending || 0,
    },
    sources: sourcePath.map(src => ({
      path: typeof src === "object" ? src.path : src,
      status: typeof src === "object" ? src.status : "",
      message: typeof src === "object" ? src.message : "",
    })),
  });
}

function fetchIndexById(id) {
  return getIndexDetail(id).then(data => {
    if (!data || !data.index_id) throw new Error("Collection not found");
    return data;
  });
}

// Helper function to extract uploadId from source path
// Expected format: .../rawdata/41b08581-2b6b-47b9-af57-baa03c8168d0/...
function extractUploadIdFromSourcePath(sourcePath) {
  if (!sourcePath || !Array.isArray(sourcePath) || sourcePath.length === 0) {
    return null;
  }
  
  // Get the first source path (could be object or string)
  const firstPath = typeof sourcePath[0] === 'object' ? sourcePath[0].path : sourcePath[0];
  
  if (!firstPath) return null;
  
  // Match UUID pattern after .rawdata/
  const match = firstPath.match(/\.rawdata\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  
  return match ? match[1] : null;
}

export default function KnowledgeBuilderDetailPage() {
  const { id } = useParams();
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadId, setUploadId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalFiles, setTotalFiles] = useState(0);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileCache, setFileCache] = useState({}); // Cache fetched files
  const [ragHealth, setRagHealth] = useState(null);
  const [ragRuns, setRagRuns] = useState([]);
  const [ragAssets, setRagAssets] = useState([]);
  const [ragQuery, setRagQuery] = useState("");
  const [ragQueryResult, setRagQueryResult] = useState(null);
  const [ragTesting, setRagTesting] = useState(false);
  const [ragLoadError, setRagLoadError] = useState("");
  const [knowledgeSources, setKnowledgeSources] = useState([]);
  const [sourceLoadError, setSourceLoadError] = useState("");
  const [addingSource, setAddingSource] = useState(false);
  const [sourceForm, setSourceForm] = useState({
    display_name: "",
    source_type: "tool_query",
    tool_name: "",
    capability: "semantic_search",
  });
  const indexRef = useRef(null);

  const fetchRagObservability = async (indexId) => {
    if (!indexId) return;
    try {
      setRagLoadError("");
      const [assetsPayload, runsPayload] = await Promise.all([
        listIndexAssets(indexId, 0, 8),
        listIndexRuns(indexId, 8),
      ]);
      setRagAssets(assetsPayload?.assets || []);
      setRagRuns(runsPayload?.runs || []);
    } catch (e) {
      setRagLoadError(e.message || "DocRAG observability is not available yet.");
    }
  };

  const fetchKnowledgeSources = async (indexId) => {
    if (!indexId) return;
    try {
      setSourceLoadError("");
      const payload = await listKnowledgeSpaceSources(indexId);
      setKnowledgeSources(payload?.sources || []);
    } catch (e) {
      setSourceLoadError(e.message || "Knowledge Source adapters are not available yet.");
    }
  };

  const setFreshIndex = (nextIndex) => {
    const previousSignature = getIndexProgressSignature(indexRef.current);
    const nextSignature = getIndexProgressSignature(nextIndex);
    if (previousSignature && previousSignature !== nextSignature) {
      setFileCache({});
      setTotalFiles(0);
    }
    indexRef.current = nextIndex;
    setIndex(nextIndex);
    setRagHealth(nextIndex?.rag_health || null);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    
    // Calculate which batch of 100 files we need
    const newSkip = Math.floor((newPage * rowsPerPage) / 100) * 100;
    const currentSkip = Math.floor((page * rowsPerPage) / 100) * 100;
    
    // If we need a different batch, fetch it
    if (newSkip !== currentSkip) {
      await fetchFilesForPage(newSkip);
    }
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Fetch first batch if not cached
    if (!fileCache[0]) {
      await fetchFilesForPage(0);
    }
  };

  // Fetch files for a specific skip offset
  const fetchFilesForPage = async (skip) => {
    if (!index || !index.source_path || !Array.isArray(index.source_path)) {
      console.log('No collection or source_path available:', { index, source_path: index?.source_path });
      return;
    }

    // Check if we already have this batch cached
    if (fileCache[skip]) {
      console.log(`Using cached files for skip=${skip}`);
      return;
    }

    console.log(`Fetching files for skip=${skip}, collection source_paths:`, index.source_path);
    setLoadingFiles(true);
    const batchFiles = [];
    let total = 0;

    for (const src of index.source_path) {
      const path = src.path || '';
      const uuidMatch = path.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      
      if (uuidMatch) {
        const uploadId = uuidMatch[1];
        console.log(`Found uploadId: ${uploadId}, fetching files...`);
        try {
          const response = await listCollectionFiles(uploadId, skip, 100);
          if (response && response.files) {
            response.files.forEach(file => {
              batchFiles.push({
                ...file,
                status: src.status,
                message: src.message,
                uploadId: uploadId
              });
            });
            // Use total from first response (assumes all uploads have same total - adjust if needed)
            if (total === 0) {
              total = response.total;
            }
          }
        } catch (error) {
          console.error(`Failed to fetch files for ${uploadId}:`, error);
        }
      } else {
        console.log('No UUID found in path:', path);
      }
    }

    console.log(`Fetched ${batchFiles.length} files, total: ${total}`);
    // Update cache
    setFileCache(prev => ({ ...prev, [skip]: batchFiles }));
    setTotalFiles(total);
    setLoadingFiles(false);
  };

  // Fetch initial files when index loads
  useEffect(() => {
    if (index) {
      console.log('Collection loaded, fetching initial files...');
      fetchFilesForPage(0);
    }
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get files for current page from cache
  const getCurrentPageFiles = () => {
    const skip = Math.floor((page * rowsPerPage) / 100) * 100;
    const cachedBatch = fileCache[skip] || [];
    
    // Calculate offset within the cached batch
    const offsetInBatch = (page * rowsPerPage) % 100;
    return cachedBatch.slice(offsetInBatch, offsetInBatch + rowsPerPage);
  };

  const paginatedFiles = getCurrentPageFiles();
  const sourceEntries = Array.isArray(index?.source_path) ? index.source_path : [];
  const active = ACTIVE_INDEX_STATUSES.has(normalizeStatus(index?.status));
  const health = ragHealth || index?.rag_health || {};
  const progress = getProgressSummary(index);
  const modalities = health?.modalities || {};
  const latestRun = health?.latest_run || null;

  const handleDownloadFile = async (file) => {
    try {
      // Use the backend API proxy to serve the file
      const blob = await downloadCollectionFile(file.uploadId, file.path);
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const refreshIndex = async () => {
    try {
      const idx = await fetchIndexById(id);
      setFreshIndex(idx);
      fetchRagObservability(idx.index_id);
      fetchKnowledgeSources(idx.index_id);
      
      // Extract uploadId from source path if not already set
      if (!uploadId && idx.source_path) {
        const extractedUploadId = extractUploadIdFromSourcePath(idx.source_path);
        if (extractedUploadId) {
          setUploadId(extractedUploadId);
        } else {
          // Generate new UUID if extraction failed
          setUploadId(uuidv4());
        }
      }
    } catch (e) {
      console.error("Failed to refresh index:", e);
    }
  };

  const handleRunRetrievalTest = async () => {
    const query = ragQuery.trim();
    if (!query || !index?.index_id) return;
    setRagTesting(true);
    setRagQueryResult(null);
    try {
      const result = await testIndexQuery(index.index_id, {
        query,
        k: 6,
        summarize: false,
        include_context: true,
      });
      setRagQueryResult(result);
      fetchRagObservability(index.index_id);
    } catch (e) {
      setRagQueryResult({ error: e.message || "Retrieval test failed.", evidence: [] });
    } finally {
      setRagTesting(false);
    }
  };

  useEffect(() => {
    let poller;
    let isMounted = true;
    indexRef.current = null;
    const fetchIndex = async () => {
      try {
        const idx = await fetchIndexById(id);
        if (!isMounted) return;
        setFreshIndex(idx);
        fetchRagObservability(idx.index_id);
        fetchKnowledgeSources(idx.index_id);
        setLoading(false);
        
        // Extract uploadId from source path on initial load
        if (idx.source_path) {
          const extractedUploadId = extractUploadIdFromSourcePath(idx.source_path);
          if (extractedUploadId) {
            setUploadId(extractedUploadId);
          } else {
            // Generate new UUID if extraction failed
            setUploadId(uuidv4());
          }
        } else {
          // No source path, generate new UUID
          setUploadId(uuidv4());
        }
        
        // Pending/queued means the task has not been picked up yet, so keep
        // polling until the index reaches a terminal state.
        if (ACTIVE_INDEX_STATUSES.has(idx.status)) {
          poller = setTimeout(fetchIndex, POLL_INTERVAL_BASE);
        }
      } catch (e) {
        if (!isMounted) return;
        setError(e.message || "Failed to fetch index");
        setLoading(false);
      }
    };
    // Only show spinner on first load
    if (!indexRef.current) setLoading(true);
    fetchIndex();
    return () => {
      isMounted = false;
      if (poller) clearTimeout(poller);
    };
  }, [id]);

  const handleAddToolSource = async () => {
    if (!index?.index_id || !sourceForm.tool_name.trim()) return;
    setAddingSource(true);
    setSourceLoadError("");
    try {
      await createKnowledgeSpaceSource(index.index_id, {
        display_name: sourceForm.display_name.trim() || sourceForm.tool_name.trim(),
        source_type: sourceForm.source_type,
        query_mode: "live",
        status: "ready",
        adapter: "tool_query",
        backing_ref: {
          tool_name: sourceForm.tool_name.trim(),
          argument_map: {
            query: "query",
            k: "limit",
          },
        },
        capabilities: [sourceForm.capability.trim() || "semantic_search"],
        freshness_policy: "live",
        permission_scope: "tenant",
      });
      setSourceForm({ display_name: "", source_type: "tool_query", tool_name: "", capability: "semantic_search" });
      await fetchKnowledgeSources(index.index_id);
    } catch (e) {
      setSourceLoadError(e.message || "Failed to add Knowledge Source.");
    } finally {
      setAddingSource(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
            color: colors.muted,
          }}
        >
          <CircularProgress sx={{ color: colors.copper }} />
          <Typography sx={{ mt: 2, fontWeight: 800 }}>Loading knowledge space...</Typography>
        </Paper>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: UI_RADIUS.panel, border: `1px solid ${colors.border}` }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </Box>
    );
  }
  if (!index) {
    return null;
  }

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2.5, flexWrap: { xs: "wrap", md: "nowrap" } }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h3" component="h1" sx={{ ...PAGE_TYPOGRAPHY.heading, overflowWrap: "anywhere" }}>
                {index.name || "Untitled space"}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.muted, mt: 0.5, maxWidth: 820 }}>
                {index.description || "No description yet."}
              </Typography>
            </Box>
            <Stack spacing={0.8} alignItems={{ xs: "flex-start", md: "flex-end" }} sx={{ flexShrink: 0, pt: { xs: 0, md: 0.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "flex-start", md: "flex-end" }} useFlexGap flexWrap="wrap">
                <StatusChip status={index.status} />
                <Chip
                  label={index.type === "code" ? "Code" : "Documents"}
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${colors.border}`,
                    borderRadius: UI_RADIUS.chip,
                    color: colors.muted,
                    fontWeight: 800,
                  }}
                />
              </Stack>
              <Button
                component={Link}
                to="/kb"
                startIcon={<ArrowBackIcon />}
                size="small"
                sx={{
                  alignSelf: { xs: "flex-start", md: "flex-end" },
                  color: colors.copperDark,
                  fontWeight: 800,
                  minHeight: 28,
                  px: 0.5,
                  "&:hover": { bgcolor: "transparent", color: colors.copper },
                }}
              >
                Back to Knowledge Spaces
              </Button>
            </Stack>
          </Box>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          <Tooltip title="Refresh status">
            <IconButton
              onClick={refreshIndex}
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: UI_RADIUS.iconButton,
                color: colors.copperDark,
                bgcolor: "#fff",
                "&:hover": { bgcolor: colors.copperSoft },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {uploadId && (
            <UpdateIndexButton
              indexId={index.index_id}
              indexName={index.name}
              indexDetails={index}
              uploadId={uploadId}
              variant="contained"
              onUpdateComplete={refreshIndex}
            />
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
          gap: PAGE_SHELL.sectionGap,
          mb: PAGE_SHELL.sectionGap,
        }}
      >
        <MetricTile icon={FolderOpenOutlinedIcon} label="Sources" value={sourceEntries.length} subtext="Uploaded groups" />
        <MetricTile icon={InsertDriveFile} label="Files" value={totalFiles || 0} subtext={loadingFiles ? "Loading..." : "Indexed uploads"} tone={colors.blue} />
        <MetricTile icon={CheckCircleOutlineIcon} label="Created" value={formatDate(index.created_at)} subtext="Space created" tone={colors.green} />
        <MetricTile icon={RefreshIcon} label="Updated" value={formatDate(index.updated_at)} subtext="Latest activity" tone={colors.amber} />
      </Box>

      {index.type === "doc" && (
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: PAGE_SHELL.sectionGap,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            bgcolor: "#fff",
          }}
        >
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} alignItems="stretch">
            <Box sx={{ flex: 1.15, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <ManageSearchIcon sx={{ color: colors.copper }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: colors.ink }}>
                    Retrieval Intelligence
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.muted }}>
                    Assets, segments, and retrieval readiness for this knowledge space.
                  </Typography>
                </Box>
                {latestRun && <StatusChip status={latestRun.status} />}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 1.2,
                  mb: 2,
                }}
              >
                <CompactMetric icon={InsertDriveFile} label="Assets" value={formatCount(health.assets)} subtext={`${formatCount(health.failed_assets)} failed`} />
                <CompactMetric icon={TimelineIcon} label="Segments" value={formatCount(health.segments)} subtext={`${formatCount(health.embeddings)} embeddings`} tone={colors.blue} />
                <CompactMetric icon={ImageOutlinedIcon} label="Images" value={formatCount(modalities.image)} subtext="Visual assets" tone={colors.green} />
                <CompactMetric icon={MovieOutlinedIcon} label="Videos" value={formatCount(modalities.video)} subtext="Frame samples" tone={colors.amber} />
              </Box>
              {ragLoadError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {ragLoadError}
                </Alert>
              )}
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.ink }}>
                  Recent ingestion runs
                </Typography>
                {ragRuns.length ? (
                  <Stack spacing={1}>
                    {ragRuns.slice(0, 4).map(run => (
                      <Box
                        key={run.id}
                        sx={{
                          p: 1.3,
                          borderRadius: UI_RADIUS.card,
                          bgcolor: colors.panel,
                          border: `1px solid ${colors.line}`,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.2}>
                          <StatusChip status={run.status} />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 900, color: colors.ink }}>
                              {formatCount(run.segment_count)} segments · {formatCount(run.asset_count)} assets
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.muted }}>
                              {formatDate(run.started_at)}{run.message ? ` · ${run.message}` : ""}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: colors.muted }}>
                    No DocRAG ingestion runs have been recorded yet.
                  </Typography>
                )}
              </Stack>
              <Divider sx={{ borderColor: colors.line, my: 2 }} />
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.ink }}>
                  Asset sample
                </Typography>
                {ragAssets.length ? (
                  <Stack spacing={0.8}>
                    {ragAssets.slice(0, 4).map(asset => (
                      <Stack
                        key={asset.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <Box sx={{ color: colors.copper, display: "flex" }}>
                          <ModalityIcon modality={asset.modality} />
                        </Box>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 800 }}>
                          {asset.filename}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.muted }}>
                          {asset.modality} · {formatBytes(asset.file_size_bytes)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: colors.muted }}>
                    No typed assets have been captured yet.
                  </Typography>
                )}
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "block" }, borderColor: colors.line }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <PlayArrowIcon sx={{ color: colors.copper }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: colors.ink }}>
                    Test Retrieval
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.muted }}>
                    Ask a question and inspect the evidence agents will receive.
                  </Typography>
                </Box>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mb: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={ragQuery}
                  onChange={(e) => setRagQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRunRetrievalTest();
                  }}
                  placeholder="Search this knowledge space..."
                  sx={formControlSx}
                />
                <Button
                  variant="contained"
                  startIcon={ragTesting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <PlayArrowIcon />}
                  onClick={handleRunRetrievalTest}
                  disabled={ragTesting || !ragQuery.trim()}
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontWeight: 900,
                    borderRadius: UI_RADIUS.button,
                    px: 2.4,
                    minWidth: 132,
                    textTransform: "none",
                    boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
                    "&:hover": { bgcolor: "primary.dark", boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}` },
                  }}
                >
                  Test
                </Button>
              </Stack>
              {ragQueryResult && (
                <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${colors.line}`, borderRadius: UI_RADIUS.panel, bgcolor: colors.panel }}>
                  {ragQueryResult.error ? (
                    <Alert severity="error">{ragQueryResult.error}</Alert>
                  ) : (
                    <Stack spacing={1.2}>
                      <Typography variant="body2" sx={{ fontWeight: 900, color: colors.ink }}>
                        {formatCount(ragQueryResult.count)} evidence item{Number(ragQueryResult.count) === 1 ? "" : "s"}
                        {ragQueryResult.latency_ms ? ` · ${ragQueryResult.latency_ms} ms` : ""}
                      </Typography>
                      {(ragQueryResult.evidence || []).slice(0, 3).map((item, idx) => (
                        <Box key={`${item.source || item.filename}-${idx}`} sx={{ p: 1.2, borderRadius: UI_RADIUS.card, bgcolor: "#fff", border: `1px solid ${colors.line}` }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.6 }}>
                            <Box sx={{ color: colors.copper, display: "flex" }}>
                              <ModalityIcon modality={item.modality} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 900, color: colors.ink, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {item.filename || item.source || "Evidence"}
                            </Typography>
                            <Chip
                              size="small"
                              label={item.modality || "text"}
                              sx={{ bgcolor: colors.copperSoft, color: colors.copperDark, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ color: colors.muted, display: "block" }}>
                            {(item.text || "").slice(0, 260) || "No snippet available."}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              )}
            </Box>
          </Stack>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${colors.border}`,
          bgcolor: "#fff",
        }}
      >
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <ManageSearchIcon sx={{ color: colors.copper }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: colors.ink }}>
                  Knowledge Sources
                </Typography>
                <Typography variant="body2" sx={{ color: colors.muted }}>
                  Governed source adapters agents can query through query_knowledge.
                </Typography>
              </Box>
              <Chip
                label={`${knowledgeSources.length} source${knowledgeSources.length === 1 ? "" : "s"}`}
                sx={{ bgcolor: colors.copperSoft, color: colors.copperDark, borderRadius: UI_RADIUS.chip, fontWeight: 900 }}
              />
            </Stack>
            {sourceLoadError && <Alert severity="warning" sx={{ mb: 1.5 }}>{sourceLoadError}</Alert>}
            {knowledgeSources.length ? (
              <Stack spacing={1.2}>
                {knowledgeSources.map((source) => (
                  <Box
                    key={source.id}
                    sx={{
                      p: 1.4,
                      borderRadius: UI_RADIUS.card,
                      border: `1px solid ${colors.line}`,
                      bgcolor: colors.panel,
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "flex-start", sm: "center" }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900, color: colors.ink }}>
                          {knowledgeSourceLabel(source)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.muted }}>
                          {readableSourceType(source.source_type)} · {source.query_mode || "indexed"} · {source.freshness_policy || "cached"}
                          {source.backing_ref?.tool_name ? ` · ${source.backing_ref.tool_name}` : ""}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                        {(source.capabilities || []).slice(0, 2).map((capability) => (
                          <Chip
                            key={capability}
                            size="small"
                            label={readableSourceType(capability)}
                            sx={{ bgcolor: "#fff", border: `1px solid ${colors.line}`, borderRadius: UI_RADIUS.chip, fontWeight: 800 }}
                          />
                        ))}
                        <StatusChip status={source.status} />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: colors.muted }}>
                No governed Knowledge Source adapters have been registered yet.
              </Typography>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "block" }, borderColor: colors.line }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.ink, mb: 0.5 }}>
              Add live source
            </Typography>
            <Typography variant="body2" sx={{ color: colors.muted, mb: 1.5 }}>
              Register a validated Tool Forge query tool as a live source inside this space.
            </Typography>
            <Stack spacing={1.2}>
              <TextField
                size="small"
                label="Display name"
                value={sourceForm.display_name}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, display_name: event.target.value }))}
                sx={formControlSx}
              />
              <TextField
                select
                size="small"
                label="Source type"
                value={sourceForm.source_type}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, source_type: event.target.value }))}
                sx={formControlSx}
              >
                <MenuItem value="tool_query">Tool-backed query</MenuItem>
                <MenuItem value="connector">Connector</MenuItem>
                <MenuItem value="database">Database</MenuItem>
                <MenuItem value="api">API</MenuItem>
              </TextField>
              <TextField
                required
                size="small"
                label="Tool name"
                placeholder="aurivio_data_lake_query"
                value={sourceForm.tool_name}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, tool_name: event.target.value }))}
                helperText="Use the published tool function name. Agents still call query_knowledge."
                sx={formControlSx}
              />
              <TextField
                size="small"
                label="Capability"
                value={sourceForm.capability}
                onChange={(event) => setSourceForm((prev) => ({ ...prev, capability: event.target.value }))}
                sx={formControlSx}
              />
              <Button
                variant="contained"
                onClick={handleAddToolSource}
                disabled={addingSource || !sourceForm.tool_name.trim()}
                startIcon={addingSource ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ManageSearchIcon />}
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  fontWeight: 900,
                  borderRadius: UI_RADIUS.button,
                  textTransform: "none",
                  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
                  "&:hover": { bgcolor: "primary.dark", boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}` },
                }}
              >
                Add source
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {active && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: PAGE_SHELL.sectionGap,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            bgcolor: colors.panel,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900, color: colors.ink }}>
                Processing knowledge space
              </Typography>
              <Typography variant="body2" sx={{ color: colors.muted }}>
                {progress.label || (index.message ? index.message.replace(/indexing/gi, "processing") : "We are preparing this space for retrieval.")}
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 260 } }}>
              <LinearProgress
                variant={progress.determinate ? "determinate" : "indeterminate"}
                value={progress.percent}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  bgcolor: colors.line,
                  "& .MuiLinearProgress-bar": { bgcolor: colors.copper, borderRadius: 99 },
                }}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1.35fr" }, gap: PAGE_SHELL.sectionGap }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            bgcolor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <FolderOpenOutlinedIcon sx={{ color: colors.copper }} />
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.ink }}>
              Source Activity
            </Typography>
          </Stack>
          <Divider sx={{ borderColor: colors.line, mb: 1.5 }} />
          {sourceEntries.length ? (
            <Stack spacing={1.4}>
              {sourceEntries.map((source, idx) => {
                const sourceStatus = typeof source === "object" ? source.status : index.status;
                const message = typeof source === "object" ? source.message : "";
                return (
                  <Box
                    key={`${sourceLabel(source)}-${idx}`}
                    sx={{
                      p: 1.5,
                      borderRadius: UI_RADIUS.card,
                      border: `1px solid ${colors.line}`,
                      bgcolor: colors.panel,
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <InsertDriveFile sx={{ color: colors.copper, fontSize: 20 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900, color: colors.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {sourceLabel(source)}
                        </Typography>
                        {message && (
                          <Typography variant="caption" sx={{ color: colors.muted }}>
                            {String(message).replace(/indexing/gi, "processing")}
                          </Typography>
                        )}
                      </Box>
                      <StatusChip status={sourceStatus || index.status} />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: colors.muted }}>
              No source activity has been recorded yet.
            </Typography>
          )}

          <Divider sx={{ borderColor: colors.line, my: 2 }} />
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 800 }}>Space ID</Typography>
            <Typography variant="body2" sx={{ color: colors.ink, fontFamily: "monospace", wordBreak: "break-all" }}>
              {index.index_id}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            bgcolor: "#fff",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
            <InsertDriveFile sx={{ color: colors.copper }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, color: colors.ink }}>
                Uploaded Files
              </Typography>
              <Typography variant="body2" sx={{ color: colors.muted }}>
                Browse and download source files in this space.
              </Typography>
            </Box>
            <Chip
              label={`${totalFiles || 0} files`}
              sx={{ bgcolor: colors.copperSoft, color: colors.copperDark, borderRadius: UI_RADIUS.chip, fontWeight: 900 }}
            />
          </Stack>

          {loadingFiles ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, p: 4, color: colors.muted }}>
              <CircularProgress size={22} sx={{ color: colors.copper }} />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Loading files...</Typography>
            </Box>
          ) : totalFiles > 0 ? (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: colors.panel }}>
                      <TableCell sx={{ fontWeight: 900, color: colors.muted, borderColor: colors.line }}>File</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900, color: colors.muted, borderColor: colors.line }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedFiles.map((file, idx) => (
                      <TableRow key={`${file.path || file.filename}-${idx}`} hover>
                        <TableCell sx={{ borderColor: colors.line }}>
                          <Stack direction="row" spacing={1.2} alignItems="center">
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: UI_RADIUS.panel,
                                bgcolor: colors.copperSoft,
                                color: colors.copperDark,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <InsertDriveFile fontSize="small" />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: colors.ink, wordBreak: "break-word" }}>
                              {file.filename}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ borderColor: colors.line }}>
                          <Tooltip title="Download file">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(file)}
                              sx={{
                                color: colors.copperDark,
                                border: `1px solid ${colors.border}`,
                                borderRadius: UI_RADIUS.iconButton,
                                bgcolor: "#fff",
                                "&:hover": { bgcolor: colors.copperSoft },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalFiles}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <Box sx={{ p: 4, textAlign: "center", color: colors.muted }}>
              <InsertDriveFile sx={{ color: colors.copper, fontSize: 42, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 900, color: colors.ink }}>No files yet</Typography>
              <Typography variant="body2">Upload files to populate this knowledge space.</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
